import React, { useState, useEffect } from 'react';
import {
  Modal,
  Input,
  Tag,
  Select,
  DatePicker,
  Space,
  Button,
  Row,
  Col,
  Typography,
  message
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMagnifyingGlass,
  faPhone,
  faCalendar,
  faHeart
} from '@fortawesome/free-solid-svg-icons';
import { COLORS } from '../../../constants/colors';
import caseService from '../../../services/CaseService';
import { MemberSelectionTable } from '../styled';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const MemberSelectionModal = ({ visible, onClose, onSelectMember }) => {
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    riskLevel: 'all',
    needsOutreach: false,
    dateRange: null
  });

  useEffect(() => {
    if (visible) {
      searchMembers();
    }
  }, [visible, filters, pagination.current]);

  const searchMembers = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        limit: pagination.pageSize
      };

      if (filters.search) {
        params.search = filters.search;
      }
      if (filters.riskLevel && filters.riskLevel !== 'all') {
        params.riskLevel = filters.riskLevel;
      }
      if (filters.needsOutreach) {
        params.needsOutreach = true; // Score >= 50
      }
      if (filters.dateRange && filters.dateRange.length === 2) {
        params.dateFrom = filters.dateRange[0].toISOString();
        params.dateTo = filters.dateRange[1].toISOString();
      }

      const response = await caseService.searchMembers(params);
      
      setMembers(response.members || []);
      setPagination({
        ...pagination,
        total: response.total || 0
      });
    } catch (error) {
      console.error('Error searching members:', error);
      message.error('Failed to search members');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    searchMembers();
  };

  const handleTableChange = (newPagination) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: pagination.total
    });
  };

  const handleSelectMember = (member) => {
    if (onSelectMember) {
      onSelectMember(member);
    }
    onClose();
  };

  const getRiskColor = (riskLevel) => {
    const colors = {
      'critical': '#ff4d4f',
      'high': '#fa8c16',
      'medium': '#faad14',
      'low': '#52c41a',
      'minimal': '#1890ff'
    };
    return colors[riskLevel] || '#d9d9d9';
  };

  const columns = [
    {
      title: 'Member',
      key: 'member',
      width: 250,
      render: (_, record) => {
        // Handle both API formats: concatenated 'name' or separate 'firstName'/'lastName'
        const displayName = record.name || `${record.firstName || ''} ${record.lastName || ''}`.trim() || 'Unknown';
        const displayPhone = record.phone || record.phoneNumber || 'N/A';
        
        return (
          <div>
            <div style={{ fontWeight: 600, fontSize: '14px' }}>
              {displayName}
            </div>
            <div style={{ color: COLORS.textMedium, fontSize: '12px' }}>
              <FontAwesomeIcon icon={faPhone} style={{ marginRight: 4 }} />
              {displayPhone}
            </div>
          </div>
        );
      }
    },
    {
      title: 'Risk Assessment',
      key: 'risk',
      width: 200,
      render: (_, record) => (
        <div>
          <Tag 
            color={getRiskColor(record.riskLevel)} 
            style={{ marginBottom: 4, fontWeight: 600 }}
          >
            {record.riskLevel ? record.riskLevel.toUpperCase() : 'NOT ASSESSED'}
          </Tag>
          {record.riskScore !== undefined && (
            <div style={{ fontSize: '12px', color: COLORS.textMedium }}>
              Score: <strong>{record.normalizedScore || record.normalizedRiskScore || 0}</strong> / 100
            </div>
          )}
          {record.criticalFindings && record.criticalFindings.length > 0 && (
            <Tag color="red" size="small" style={{ fontSize: '10px', marginTop: 4 }}>
              ⚠ {record.criticalFindings.length} Critical Finding{record.criticalFindings.length > 1 ? 's' : ''}
            </Tag>
          )}
        </div>
      ),
      sorter: (a, b) => (a.riskScore || 0) - (b.riskScore || 0)
    },
    {
      title: 'Last Visit',
      key: 'lastVisit',
      width: 150,
      render: (_, record) => {
        if (!record.lastVisit) {
          return <Text type="secondary">No visits</Text>;
        }
        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <FontAwesomeIcon icon={faCalendar} style={{ color: COLORS.textMedium }} />
              <span>{new Date(record.lastVisit.visitDate).toLocaleDateString()}</span>
            </div>
            {record.lastVisit.eventName && (
              <div style={{ fontSize: '11px', color: COLORS.textMedium, marginTop: 2 }}>
                {record.lastVisit.eventName}
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Needs Outreach',
      key: 'needsOutreach',
      width: 120,
      align: 'center',
      render: (_, record) => {
        const needsOutreach = record.riskScore >= 50 || 
                             (record.criticalFindings && record.criticalFindings.length > 0);
        return needsOutreach ? (
          <Tag 
            icon={<FontAwesomeIcon icon={faHeart} />}
            style={{
              backgroundColor: '#000',
              color: '#fff',
              border: 'none',
              fontWeight: 600
            }}
          >
            YES
          </Tag>
        ) : (
          <Tag color="default">No</Tag>
        );
      }
    },
    {
      title: 'Action',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Button 
          type="primary"
          onClick={() => handleSelectMember(record)}
          style={{
            height: '35px',
            padding: '0 16px',
            fontWeight: 600,
            borderRadius: '6px',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease'
          }}
        >
          Select
        </Button>
      )
    }
  ];

  return (
    <Modal
      title={
        <span style={{ fontSize: 18, fontWeight: 600 }}>Select Member for Outreach</span>
      }
      open={visible}
      onCancel={onClose}
      width="90%"
      style={{ maxWidth: '1000px' }}
      footer={null}
      destroyOnClose
      styles={{ body: { padding: '24px' }}}
    >
      {/* Filters */}
      <Space 
        direction="vertical" 
        size="middle" 
        style={{ width: '100%', marginBottom: 16 }}
      >
        {/* Search bar */}
        <Row gutter={[12, 12]}>
          <Col xs={24} md={18}>
            <Input
              placeholder="Search by name, ID, email, or phone..."
              prefix={<FontAwesomeIcon icon={faMagnifyingGlass} style={{ color: COLORS.textMuted }} />}
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              onPressEnter={handleSearch}
              allowClear
              style={{ borderRadius: 8, height: 40 }}
            />
          </Col>
          <Col xs={24} md={6}>
            <Button
              type="primary"
              icon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
              onClick={handleSearch}
              block
              style={{ borderRadius: 8, fontWeight: 600, height: 40 }}
            >
              Search
            </Button>
          </Col>
        </Row>

        {/* Filter row */}
        <Row gutter={[12, 12]} align="middle">
          <Col xs={12} md={5}>
            <Select
              size="large"
              placeholder="Risk Level"
              value={filters.riskLevel}
              onChange={(value) => setFilters({ ...filters, riskLevel: value })}
              style={{ width: '100%' }}
            >
              <Select.Option value="all">All Risk Levels</Select.Option>
              <Select.Option value="critical">Critical</Select.Option>
              <Select.Option value="high">High</Select.Option>
              <Select.Option value="medium">Medium</Select.Option>
              <Select.Option value="low">Low</Select.Option>
              <Select.Option value="minimal">Minimal</Select.Option>
            </Select>
          </Col>

          <Col xs={12} md={5}>
            <Select
              size="large"
              placeholder="Outreach Filter"
              value={filters.needsOutreach ? 'needs-outreach' : 'all'}
              onChange={(value) => setFilters({ ...filters, needsOutreach: value === 'needs-outreach' })}
              style={{ width: '100%' }}
            >
              <Select.Option value="all">All Members</Select.Option>
              <Select.Option value="needs-outreach">Needs Outreach (≥50)</Select.Option>
            </Select>
          </Col>

          <Col xs={24} md={10}>
            <RangePicker
              size="large"
              placeholder={['Visit Start Date', 'Visit End Date']}
              value={filters.dateRange}
              onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
              style={{ width: '100%' }}
            />
          </Col>

          <Col xs={24} md={4}>
            <Button
              size="large"
              onClick={() => {
                setFilters({
                  search: '',
                  riskLevel: 'all',
                  needsOutreach: false,
                  dateRange: null
                });
                setPagination({ ...pagination, current: 1 });
              }}
              block
              style={{ borderRadius: 8 }}
            >
              Clear
            </Button>
          </Col>
        </Row>
      </Space>

      {/* Results Table */}
      <MemberSelectionTable
        columns={columns}
        dataSource={members}
        loading={loading}
        rowKey={(record) => record.patientId || record._id}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} members`
        }}
        onChange={handleTableChange}
        scroll={{ x: 900 }}
        style={{ marginTop: 16 }}
      />
    </Modal>
  );
};

export default MemberSelectionModal;
